import { useCallback, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import getNotifications from '@cartwhl/common/api/getNotifications';
import {
  type JobNotification,
  jobNotificationStore,
} from '@cartwhl/common/stores/jobNotificationStore';
import * as notificationStore from '@cartwhl/common/stores/notificationStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import type { UserNotification } from '@cartwhl/common/types';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/TextNew';

import NotificationButton from '../NotificationButton';
import JobNotificationListItem from './components/JobNotificationListItem';
import NotificationListItem from './components/NotificationListItem';

const NOTIFICATIONS_DROPDOWN_Z_INDEX = 60;
const NOTIFICATIONS_PAGE_SIZE = 20;

export default function NotificationsMenu({
  appletHeader = false,
}: {
  appletHeader?: boolean;
}) {
  const { newNotificationCount } = useSnapshot(notificationStore.state);
  const { notifications: jobNotifications } = useSnapshot(
    jobNotificationStore.state,
  );
  const { loading: userLoading } = useSnapshot(userStore.state);
  const [open, setOpen] = useState(false);
  const viewedJobs = useRef(new Map<string, JobNotification['event']>());
  const hasRunningJobs =
    !userLoading && jobNotifications.some((item) => item.event === 'started');
  const showIndicator =
    !userLoading &&
    !open &&
    (newNotificationCount > 0 ||
      jobNotifications.some(
        (item) => item.status === 'new' && item.event !== 'started',
      ));

  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [listScrollRoot, setListScrollRoot] = useState<HTMLUListElement | null>(
    null,
  );
  const nextTokenRef = useRef<string | undefined>(undefined);
  const loadingMoreRef = useRef(false);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    nextTokenRef.current = undefined;
    setHasMore(false);

    const { data, error } = await getNotifications({
      limit: NOTIFICATIONS_PAGE_SIZE,
    });
    setLoading(false);

    if (error || !data) {
      setNotifications([]);
      return;
    }

    setNotifications(data.records);
    nextTokenRef.current = data.nextToken;
    setHasMore(Boolean(data.nextToken));
  }, []);

  const loadMore = useCallback(async () => {
    const token = nextTokenRef.current;
    if (!token || loadingMoreRef.current || loading) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);

    const { data, error } = await getNotifications({
      limit: NOTIFICATIONS_PAGE_SIZE,
      nextToken: token,
    });

    loadingMoreRef.current = false;
    setLoadingMore(false);

    if (error || !data) {
      return;
    }

    setNotifications((prev) => {
      const seen = new Set(prev.map((record) => record.notificationID));
      const appended = data.records.filter(
        (record) => !seen.has(record.notificationID),
      );
      return appended.length > 0 ? [...prev, ...appended] : prev;
    });
    nextTokenRef.current = data.nextToken;
    setHasMore(Boolean(data.nextToken));
  }, [loading]);

  const markNotificationRead = useCallback(
    async (notificationID: string) => {
      setNotifications((prev) =>
        prev.map((record) =>
          record.notificationID === notificationID
            ? { ...record, status: 'read' }
            : record,
        ),
      );

      const ok = await notificationStore.setNotificationStatus(
        notificationID,
        'read',
      );
      if (!ok) {
        void loadNotifications();
      }
    },
    [loadNotifications],
  );

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) {
      void loadNotifications();
    } else {
      // Keep the new styling during the first view; commit only rows actually seen.
      for (const [id, event] of viewedJobs.current)
        jobNotificationStore.markRead(id, event);
      viewedJobs.current.clear();
      setNotifications([]);
      nextTokenRef.current = undefined;
      setHasMore(false);
    }
  };

  const markJobViewed = useCallback(
    (id: string, event: JobNotification['event']) => {
      viewedJobs.current.set(id, event);
    },
    [],
  );
  const entries = [...notifications, ...jobNotifications].sort(
    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
  );

  const showInitialLoading = loading && entries.length === 0;
  const showList = !showInitialLoading && entries.length > 0;

  return (
    <Dropdown
      side="bottom"
      contentVariant="panel"
      align="end"
      zIndex={NOTIFICATIONS_DROPDOWN_Z_INDEX}
      onOpenChange={handleOpenChange}
      width={appletHeader ? undefined : 340}
      trigger={
        <NotificationButton
          showIndicator={showIndicator}
          processing={hasRunningJobs}
          appletHeader={appletHeader}
        />
      }
    >
      <div className="flex w-[min(100vw-2rem,340px)] flex-col">
        <div className="border-primary border-b border-solid px-4 py-3">
          <Text token="14/medium" className="text-primary">
            Notifications
          </Text>
        </div>
        {showInitialLoading ? (
          <Text token="14" className="text-tertiary px-4 py-6">
            Loading…
          </Text>
        ) : entries.length === 0 ? (
          <Text token="14" className="text-tertiary px-4 py-6">
            Your notifications will appear here.
          </Text>
        ) : null}
        {showList ? (
          <ul
            ref={setListScrollRoot}
            className="styled-scrollbar max-h-[min(70vh,420px)] overflow-y-auto"
          >
            {entries.map((notification) =>
              'batchID' in notification ? (
                <JobNotificationListItem
                  key={notification.batchID}
                  notification={notification}
                  scrollRoot={listScrollRoot}
                  onViewed={markJobViewed}
                />
              ) : (
                <NotificationListItem
                  key={notification.notificationID}
                  notification={notification}
                  onMarkRead={markNotificationRead}
                />
              ),
            )}
            {hasMore ? (
              <li className="flex list-none justify-center py-2">
                <InfiniteLoadingTrigger
                  loading={loadingMore}
                  onEnter={() => void loadMore()}
                  root={listScrollRoot}
                  rootMargin="0px 0px 200px 0px"
                />
              </li>
            ) : null}
          </ul>
        ) : null}
      </div>
    </Dropdown>
  );
}
